import { create } from 'zustand';
import type { Point, RenderedIndicator } from '../indicators/instances';
import type { Bar, Timeframe } from './chartModel';

/** One indicator's values on one bar, as MT5's Data Window lists them. */
export interface IndicatorReadout {
  uid: string;
  title: string;
  values: { label: string; value: number | null; text: string; color: string }[];
}

/** The point drawn at exactly this time, or null (the lists are sorted by time). */
export function pointAt(points: readonly Point[], time: number): Point | null {
  let lo = 0;
  let hi = points.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = points[mid]!.time;
    if (t === time) return points[mid]!;
    if (t < time) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
}

/**
 * How an indicator value is written: price-like values of a trend indicator with the symbol's digits, the rest with four
 * decimals, or two once they are large (volumes, A/D), without trailing noise.
 */
export function formatIndicatorValue(value: number, overlay: boolean, digits: number): string {
  if (overlay) return value.toFixed(digits);
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  return Number(value.toFixed(Math.max(4, digits))).toString();
}

/** Every indicator's values on the bar that opens at `time` (lines that are shifted show the value drawn there). */
export function readout(
  indicators: readonly RenderedIndicator[],
  time: number,
  digits: number,
): IndicatorReadout[] {
  return indicators.map((ind) => ({
    uid: ind.uid,
    title: ind.title,
    values: ind.outputs.map((o) => {
      const p = pointAt(o.points, time);
      return {
        label: o.label,
        value: p?.value ?? null,
        text: p ? formatIndicatorValue(p.value, ind.overlay, digits) : '—',
        color: p?.color ?? o.color,
      };
    }),
  }));
}

export interface DataWindowState {
  symbol: string | null;
  tf: Timeframe | null;
  digits: number;
  bar: Bar | null;
  indicators: IndicatorReadout[];
  publish: (next: Omit<DataWindowState, 'publish'>) => void;
}

/** What the chart under the cursor (or the active chart) shows for its bar; the Data Window panel reads it. */
export const useDataWindow = create<DataWindowState>((set) => ({
  symbol: null,
  tf: null,
  digits: 5,
  bar: null,
  indicators: [],
  publish: (next) => set(next),
}));
